import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { cor, texto } from '#components/finapp/estilo';
import type { MediaDeCategoria } from '#components/finapp/motor/medias';

type Props = {
  media: MediaDeCategoria;
  /** Gasto desta categoria no mês corrente, em centavos, positivo. */
  esteMes: number;
  /**
   * Celular. Vem de `useResponsive()` em `Medias.tsx`, descendo por
   * `BlocoDeTipo` — mesma cadeia que `Inicio.tsx` → `inicio/Heroi.tsx` já
   * usa.
   *
   * ⚠️⚠️ Validação no navegador (22/09/2026) — em 390px de largura, a linha
   * (nome · média · este mês · diferença, os três últimos com `minWidth`
   * fixo) passava dos 390px e a diferença era cortada pelo
   * `overflow-x: hidden` do body, sem scroll possível. Sem isso, o dono via
   * a categoria e a média, mas nunca o número que mais importa no celular
   * (quanto ele já gastou ACIMA/ABAIXO do normal).
   */
  estreito?: boolean;
};

/**
 * Uma categoria: nome · média mensal · este mês · diferença.
 *
 * ⚠️ Sem borda separando as linhas (regra nº 1 da estética do dono): o
 * espaçamento é que separa.
 *
 * ⚠️⚠️ Revisão final da branch (Finding 6, Minor) — o sinal da COR se
 * INVERTE dentro do balde Investimento: em todo outro balde, "acima da
 * média" é vermelho (gastou mais que o normal, ruim) e "abaixo" é verde
 * (bom). Dentro de Investimento, gastar mais é APORTAR mais — o passo
 * final da história do dono ("quanto consegui guardar") — e pintar isso de
 * vermelho diria o oposto do que aconteceu. `media.tipo` já vem em
 * `MediaDeCategoria` (não precisou de uma prop nova de `BlocoDeTipo`: o
 * dado que decide o balde já estava disponível aqui).
 */
export function LinhaDeMedia({ media, esteMes, estreito = false }: Props) {
  const { t } = useTranslation();
  const diferenca = esteMes - media.media;
  const acimaDaMedia = diferenca > 0;
  const ehInvestimento = media.tipo === 'investimento';
  const corDiferenca = ehInvestimento
    ? acimaDaMedia
      ? cor.entrada
      : cor.saida
    : acimaDaMedia
      ? cor.saida
      : cor.entrada;

  return (
    <View
      style={{
        flexDirection: estreito ? 'column' : 'row',
        alignItems: estreito ? 'flex-start' : 'baseline',
        justifyContent: 'space-between',
        gap: estreito ? 4 : 0,
        paddingTop: 10,
        paddingBottom: 10,
      }}
      data-testid={`linha-media-${media.categoriaId}`}
    >
      <Text style={texto.corpo}>{media.nome}</Text>

      <View
        style={{
          flexDirection: 'row',
          gap: estreito ? 12 : 18,
          alignItems: 'baseline',
          flexWrap: estreito ? 'wrap' : 'nowrap',
        }}
      >
        <Text style={{ ...texto.valorMedio, color: cor.tinta }}>
          {integerToCurrency(media.media)}
        </Text>
        <Text
          style={{
            ...texto.corpo,
            color: cor.tintaFraca,
            ...(estreito ? null : { minWidth: 90 }),
          }}
        >
          {t('este mês {{valor}}', { valor: integerToCurrency(esteMes) })}
        </Text>
        <Text
          style={{
            ...texto.corpo,
            color: corDiferenca,
            ...(estreito ? null : { minWidth: 80 }),
          }}
        >
          {acimaDaMedia ? '+' : ''}
          {integerToCurrency(diferenca)}
        </Text>
      </View>
    </View>
  );
}
